import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { TypedMemo } from '../../../../../common/types';
import { LoadingView } from '../../../../../common/views/LoadingView';
import { ErrorView } from '../../../../../common/views/ErrorView/ErrorView';
import { getFullAPIFileURL } from '../../../../../common/getFullAPIFileURL';
import { allFilesMessages } from './allFilesMessages';
import { AllFilesGroupId, AllFilesRow } from './allFilesModel';
import { useAllFilesSearch } from './useAllFilesSearch';
import styles from './AllFiles.module.scss';

interface AllFilesDialogProps {
  accountId: number;
  isOpen: boolean;
  songTitles: string[];
  onClose: () => void;
}

const GROUP_TITLES: Readonly<Record<AllFilesGroupId, typeof allFilesMessages.groupKeepers>> = {
  keepers: allFilesMessages.groupKeepers,
  posts: allFilesMessages.groupPosts,
  uploads: allFilesMessages.groupUploads,
};

/**
 * The "All files" search (design assets-rethink d4, decision 8): a search, not a page. Any file of
 * the artist by song or type, grouped Release kit / Posts / Your uploads, archived files included.
 * A row opens the file in its home: the Kit tab, Posts or the archive; an upload opens the file.
 */
const AllFilesDialogComponent: React.FC<AllFilesDialogProps> = ({ accountId, isOpen, songTitles, onClose }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { groups, isLoading, error } = useAllFilesSearch({ accountId, query, songTitles, enabled: isOpen });

  const open = useCallback(
    (row: AllFilesRow) => {
      if (row.target.kind === 'file') {
        window.open(getFullAPIFileURL(row.target.url), '_blank', 'noopener');
        return;
      }
      onClose();
      Promise.resolve(navigate(row.target.path)).catch(console.error);
    },
    [navigate, onClose]
  );

  let body: React.ReactNode;
  if (isLoading) {
    body = <LoadingView />;
  } else if (error !== undefined) {
    body = <ErrorView error={error as Error} />;
  } else if (groups.length === 0) {
    body = (
      <p className={styles.note}>
        {query.trim() === ''
          ? intl.formatMessage(allFilesMessages.nothing)
          : intl.formatMessage(allFilesMessages.empty, { query: query.trim() })}
      </p>
    );
  } else {
    body = groups.map((group) => (
      <section key={group.id} aria-label={intl.formatMessage(GROUP_TITLES[group.id])}>
        <div className={styles.groupHead}>{intl.formatMessage(GROUP_TITLES[group.id])}</div>
        {group.rows.map((row) => (
          <button
            key={row.key}
            type="button"
            className={styles.row}
            onClick={() => open(row)}
            data-testid="all-files-row"
          >
            {row.pictureUrl === null ? (
              <span className={styles.thumb} aria-hidden="true" />
            ) : (
              <img className={styles.thumb} src={getFullAPIFileURL(row.pictureUrl)} alt="" />
            )}
            <span className={styles.rowText}>
              <span className={styles.rowLabel}>{row.label}</span>
              <span className={styles.rowPlace}>{row.place}</span>
            </span>
          </button>
        ))}
      </section>
    ));
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" aria-label={intl.formatMessage(allFilesMessages.title)}>
      <div className={styles.searchBox}>
        <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />
        <input
          className={styles.searchInput}
          type="search"
          // The dialog opens for one task: the field takes the keys at once.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={intl.formatMessage(allFilesMessages.placeholder)}
          aria-label={intl.formatMessage(allFilesMessages.placeholder)}
          data-testid="all-files-input"
        />
        <span className={styles.esc} aria-hidden="true">
          esc
        </span>
      </div>
      <div className={styles.results}>{body}</div>
    </Modal>
  );
};

export const AllFilesDialog = TypedMemo(AllFilesDialogComponent);
